import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useIsDarkMode } from "@/hooks/theme";
import { Button } from "@/components/ui/button";
import drawDropsTimeline, { TimelineDropsDatum } from "./drawDropsTimeline";
import { playerDropCategories } from "@/lib/playerDropCategories";
import { PlayerDropsMessage } from "./PlayerDropsGenericSubcards";
import { DrilldownRangeSelectionType } from "./PlayerDropsPage";
import { numberToLocaleString } from "@/lib/utils";

export type TimelineDropsChartData = {
    displayLod: string;
    startDate: Date;
    endDate: Date;
    maxDrops: number;
    categoriesSorted: string[];
    log: TimelineDropsDatum[];
}

export type HoverDatumType = {
    datum: TimelineDropsDatum;
    isDay: boolean;
} | null;

type TimelineDropsChartProps = {
    chartData: TimelineDropsChartData;
    chartName: string;
    width: number;
    height: number;
    rangeSelected: DrilldownRangeSelectionType;
    rangeSetter: (range: DrilldownRangeSelectionType) => void;
    onHoverDatum?: (datum: HoverDatumType) => void;
};

/**
 * Hover Info Panel - React-driven replacement for the imperative floating div
 */
const HoverInfoPanel = memo(({ hoverDatum, categories }: { hoverDatum: HoverDatumType; categories: string[] }) => {
    // Always render — show hint when no datum, full panel when hovering
    if (!hoverDatum) {
        return (
            <div className="w-full mt-2 px-1">
                <div className="flex items-center justify-center gap-2 h-8 bg-white/[0.015] border border-white/5 border-dashed rounded-lg">
                    <span className="font-mono text-[9px] tracking-widest text-white/20 uppercase select-none">
                        ↑ hover chart to inspect interval
                    </span>
                </div>
            </div>
        );
    }

    const { datum, isDay } = hoverDatum;

    const dateStr = datum.startDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = datum.startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const timeLabel = isDay ? dateStr : `${dateStr} @ ${timeStr}`;

    const totalDrops = datum.drops.reduce((acc, [, cnt]) => acc + cnt, 0);
    const categoriesReversed = [...categories].reverse();

    return (
        <div className="w-full mt-2 px-1 animate-[fadeIn_0.1s_ease]">
            <div className="flex flex-col sm:flex-row items-stretch gap-3 bg-[#0d101d]/80 border border-white/8 rounded-xl p-3 backdrop-blur-md shadow-[0_0_20px_rgba(0,255,209,0.08)]">
                {/* Timestamp + change flag */}
                <div className="flex flex-col justify-center gap-1 min-w-fit pr-3 sm:border-r border-white/8">
                    <span className="font-mono text-[10px] font-bold tracking-widest text-scCyan uppercase">
                        {timeLabel}
                    </span>
                    {datum.changes ? (
                        <span className="text-[9px] font-mono font-bold tracking-widest text-[#0a0d1a] bg-scPurple rounded px-1.5 py-0.5 self-start">
                            {datum.changes > 1 ? `${datum.changes} CHANGES` : '1 CHANGE'}
                        </span>
                    ) : (
                        <span className="text-[9px] font-mono text-white/20 tracking-widest">
                            NO CHANGES
                        </span>
                    )}
                    <span className="font-mono text-xs font-bold text-white mt-1">
                        {numberToLocaleString(totalDrops)}
                        <span className="text-[9px] text-muted-foreground ml-1 font-normal">total</span>
                    </span>
                </div>

                {/* Category breakdown */}
                <div className="flex-1 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-4 gap-y-1.5 items-center">
                    {categoriesReversed.map((catName) => {
                        const catDef = playerDropCategories[catName];
                        if (!catDef) return null;
                        const catCount = datum.drops.find(([cat]) => cat === catName)?.[1] ?? 0;
                        const pct = totalDrops > 0 ? (catCount / totalDrops) * 100 : 0;
                        return (
                            <div key={catName} className="flex items-center gap-2 min-w-[120px]">
                                <div
                                    className="size-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: catDef.color, boxShadow: `0 0 6px ${catDef.color}80` }}
                                />
                                <div className="flex flex-col gap-0.5 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="font-mono text-[10px] text-slate-300 tracking-wide leading-none">
                                            {catDef.label}
                                        </span>
                                        <span className="font-mono text-[10px] font-bold text-white leading-none">
                                            {numberToLocaleString(catCount)}
                                        </span>
                                    </div>
                                    <div className="w-full h-[3px] bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all duration-300"
                                            style={{ width: `${pct}%`, backgroundColor: catDef.color }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});


function TimelineDropsChart({ chartData, chartName, width, height, rangeSelected, rangeSetter, onHoverDatum: externalOnHoverDatum }: TimelineDropsChartProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const legendRef = useRef<HTMLDivElement>(null);
    const [renderError, setRenderError] = useState('');
    const [errorRetry, setErrorRetry] = useState(0);
    const isDarkMode = useIsDarkMode();
    const margins = {
        top: 8,
        right: 8,
        bottom: 24,
        left: 42,
        axis: 1
    };

    // Callback for drawDropsTimeline to report hover datum
    // Use a ref to always call the latest external callback + capture latest displayLod
    // without making it a useEffect dep (avoids re-render loops)
    const hoverCallbackRef = useRef<{
        fn: typeof externalOnHoverDatum;
        isDay: boolean;
    }>({ fn: externalOnHoverDatum, isDay: chartData.displayLod === 'day' });
    hoverCallbackRef.current.fn = externalOnHoverDatum;
    hoverCallbackRef.current.isDay = chartData.displayLod === 'day';

    const stableOnHoverDatum = useCallback((datum: TimelineDropsDatum | null) => {
        if (!datum) {
            hoverCallbackRef.current.fn?.(null);
            return;
        }
        hoverCallbackRef.current.fn?.({
            datum,
            isDay: hoverCallbackRef.current.isDay,
        });
    }, []); // stable — empty deps, reads latest values via ref

    //Redraw chart when data or size changes
    useEffect(() => {
        if (!chartData || !legendRef.current || !svgRef.current || !canvasRef.current || !width || !height) return;
        if (!chartData.log.length) return; //only in case somehow the api returned, but no data found
        try {
            console.groupCollapsed(`Drawing player ${chartName} drops:`);
            console.time(`drawDropsTimeline-${chartName}`);
            drawDropsTimeline({
                chartName,
                legendRef: legendRef.current,
                svgRef: svgRef.current,
                canvasRef: canvasRef.current,
                size: { width, height },
                rangeSelected,
                margins,
                isDarkMode,
                data: chartData,
                setRenderError,
                rangeSetter,
                onHoverDatum: stableOnHoverDatum,
            });
            setErrorRetry(0);
            setRenderError('');
            console.timeEnd(`drawDropsTimeline-${chartName}`);
        } catch (error) {
            setRenderError((error as Error).message ?? 'Unknown error.');
        } finally {
            console.groupEnd();
        }
    }, [
        chartData,
        chartName,
        width,
        height,
        rangeSelected,
        rangeSetter,
        isDarkMode,
        stableOnHoverDatum,
    ]);

    if (!width || !height) return null;
    if (renderError) {
        return <div className="absolute inset-0 p-4 flex flex-col gap-4 items-center justify-center text-center text-lg font-mono text-destructive-inline">
            Render Error: {renderError}
            <br />
            <Button
                size={'sm'}
                variant={'outline'}
                className='text-primary'
                onClick={() => {
                    setErrorRetry(c => c + 1);
                    setRenderError('');
                }}
            >
                Retry{errorRetry ? ` (${errorRetry})` : ''}
            </Button>
        </div>
    } else if (!chartData.maxDrops) {
        return <PlayerDropsMessage message="No players disconnected from your server recently." />
    }
    return (<>
        {/* Hidden legacy legend div - kept for compat but opacity 0 always */}
        <div
            ref={legendRef}
            style={{
                zIndex: -1,
                position: 'absolute',
                top: `12px`,
                opacity: 0,
                pointerEvents: 'none',
            }}
            aria-hidden="true"
        >
            {/* Legacy tooltip content for drawDropsTimeline compat */}
            <div className="tooltip-time" />
            {chartData.categoriesSorted.slice().reverse().map((catName) => (
                <div key={catName} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-1.5">
                        <div className="size-2 rounded-full" style={{ backgroundColor: playerDropCategories[catName]?.color }} />
                        <span>{playerDropCategories[catName]?.label}</span>
                    </div>
                    <span data-category={catName} />
                </div>
            ))}
            <div className="change-flag" />
        </div>
        <svg
            ref={svgRef}
            width={width}
            height={height}
            style={{
                zIndex: 1,
                position: 'absolute',
                top: '0px',
                left: '0px',
            }}
        />
        <canvas
            ref={canvasRef}
            width={width - margins.left - margins.right}
            height={height - margins.top - margins.bottom}
            style={{
                zIndex: 0,
                position: 'absolute',
                top: `${margins.top}px`,
                left: `${margins.left}px`,
            }}
        />
    </>);
}

// Exported wrapper that exposes hoverDatum for parent
export { HoverInfoPanel };

export default memo(TimelineDropsChart);
